// 아직 보내지 않은 것을 **읽기가 다시 도는 동안** 잃지 않게.
//
// ## 왜 이 파일이 따로 있나
//
// 브라우저 검증에서 실제로 이렇게 났다: 넘김 작성기 안에서 자료를 연결하면
// `task_reference_add` 가 성공하고 → `useCommand` 가 Task 맥락을 무효화하고 →
// `useResource` 가 캐시를 지우고 loading 으로 바뀌고 → `<Resource>` 가 children 을 안 그리고 →
// `TaskView` 가 통째로 unmount 된다. 작성기 상태가 `TaskView` 안에 있었으므로, **성공한
// 연결이 사람이 쓰던 글을 지웠다.** 「그 자리에서 연결한다」는 이 흐름의 약속을 정면으로 어긴다.
//
// 고칠 자리는 무효화가 아니다. 낡은 값을 계속 그리는 것은 D-4 가 났던 방식이고, 그 규칙은
// 그대로 서 있어야 한다. **고칠 자리는 초안이 어디 사는가**다.
//
// 그래서 초안은 컴포넌트 밖의 작은 저장소에 산다. unmount 로 사라지지 않는다는 성질이
// 우연(어느 컴포넌트가 마침 살아남았다)이 아니라 **구조**가 되고, 그래서 테스트도
// 실제 무효화 경로로 그 성질을 직접 확인할 수 있다.
//
// ## 이 저장소가 하지 않는 것
//
//   · **브라우저 저장소를 쓰지 않는다.** 개인의 미전송 초안을 `localStorage` 에 두면
//     로그아웃한 기계에 남는다. 여기 있는 것은 이 탭의 메모리뿐이고, 새로고침하면 없다.
//   · **권위를 대신하지 않는다.** 초안이 남아 있다고 화면이 보호된 내용을 그리지 않는다 —
//     그 판단은 여전히 서버가 준 읽기 상태가 한다. 초안은 "사람이 아직 적고 있던 글"일 뿐이다.
//   · **범위를 넘지 않는다.** 키가 (Space, Task, 말하는 Node) 셋이라, 다른 Task 나 다른
//     Node 로 옮기면 그 초안은 **읽히지 않는다.** 지우는 것과 못 읽는 것 둘 다 해 둔다.

/** scope → (kind → value). 이 탭의 메모리뿐이다. */
const drafts = new Map();

/**
 * 초안 하나를 가리키는 자리.
 *
 * 셋 중 하나라도 다르면 다른 자리다. 특히 `nodeId` 가 들어 있는 것이 중요하다 — 사람이
 * 말하는 Node 를 바꾸면 그건 다른 사람으로서 쓰는 글이고, 앞의 초안을 이어 쓰게 두면
 * 안 된다.
 */
export function draftScope({ spaceId, taskId, nodeId }) {
  if (!spaceId || !taskId || !nodeId) return null;
  // 구분자는 `\u0000` 이다 — id 어디에도 나타날 수 없는 유일한 바이트라, 어느 칸에 무엇이
  // 들어오든 자르는 자리가 흔들리지 않는다. 소스에는 escape 로 적는다: 추적되는 파일 안의
  // 리터럴 NUL 은 git·포매터·검색 도구를 헷갈리게 한다(task-ledger 의 cursor 주석과 같은 이유).
  return `${spaceId}\u0000${taskId}\u0000${nodeId}`;
}

/** 없으면 `null`. 빈 객체를 지어내지 않는다 — "안 썼다"와 "쓰다 지웠다"는 다르다. */
export function readDraft(scope, kind) {
  if (scope === null) return null;
  return drafts.get(scope)?.get(kind) ?? null;
}

export function writeDraft(scope, kind, value) {
  if (scope === null) return;
  const forScope = drafts.get(scope) ?? new Map();
  forScope.set(kind, value);
  drafts.set(scope, forScope);
}

export function clearDraft(scope, kind) {
  drafts.get(scope)?.delete(kind);
  if (drafts.get(scope)?.size === 0) drafts.delete(scope);
}

/**
 * 이 자리의 초안을 전부 버린다.
 *
 * 부르는 자리 둘: Task·Node 가 바뀌었을 때, 그리고 **읽기가 권한으로 거절됐을 때.**
 * 두 번째가 요점이다 — 더 이상 볼 수 없게 된 Task 의 미전송 글을 메모리에 들고 있을 이유가 없다.
 */
export function clearScope(scope) {
  if (scope === null) return;
  drafts.delete(scope);
}

/** 세션이 끝났거나 말하는 Node 가 바뀌었다. 남길 것이 없다. */
export function clearAllDrafts() {
  drafts.clear();
}

/** 테스트가 저장소의 실제 크기를 볼 수 있게. 화면은 쓰지 않는다. */
export function draftScopeCount() {
  return drafts.size;
}

/**
 * 이 읽기 실패가 **초안을 버려야 하는 실패**인가.
 *
 * 잠깐 못 읽는 것(네트워크·투영 지연)과 이제 못 보는 것(권한 회수·다른 Space)은 다르다.
 * 앞의 것에서 초안을 지우면 사람은 잠깐의 오류로 쓰던 글을 잃고, 뒤의 것에서 안 지우면
 * 볼 수 없게 된 Task 의 글이 메모리에 남는다.
 *
 * 판단 근거는 **서버가 준 코드와 상태**뿐이다. 화면이 추측하지 않는다.
 */
const DENIED_CODES = new Set([
  'forbidden',
  'not_space_member',
  'task_not_found',
  'space_archived',
  'node_not_bound_to_session',
  'human_node_not_selectable',
  'session_invalid',
  'session_expired',
  'reauth_required',
]);

/**
 * **403 이라고 다 거절이 아니다.**
 *
 * `space_not_projected` 가 그렇다 — Space 를 갓 만든 직후, 권위 스냅샷이 아직 닿기 전에
 * 잠깐 나는 답이고 시간이 해결한다(Gateway 가 이 코드를 일부러 뭉개지 않고 통과시키는 이유가
 * 그것이다). 이걸 거절로 세면 방금 Space 를 만든 사람이 쓰던 글을 잃는다.
 */
const TRANSIENT_CODES = new Set([
  'space_not_projected',
  'projection_lag',
  'provider_unavailable',
  'upstream_unavailable',
  'upstream_rejected_claim',
  'resource_cloud_unavailable',
  'bridge_credentials_unavailable',
  'gateway_unreachable',
  'gateway_timeout',
]);

export function isAccessDenied(error) {
  if (!error) return false;
  if (TRANSIENT_CODES.has(error.code)) return false;
  if (DENIED_CODES.has(error.code)) return true;
  // 코드를 모르는 4xx 중 권한·부재만. 400(요청이 틀림)·409(판이 다름)는 여기 아니다.
  return error.status === 401 || error.status === 403 || error.status === 404;
}
